import axios from 'axios';
import { REQUEST_TIMEOUT, SERVICE_HOST } from '@config/constant';

import MeetingApi from '@hkgai/pb-fe-api/lib/meeting_api/meeting_api';
import {
  FileInfo,
  CreateMeetingReq,
  CreateMeetingResp,
  MeetingScene,
  DeleteMeetingReq,
  DeleteMeetingResp,
  GetAudioRecognitionReq,
  GetAudioRecognitionResp,
  GetFileObsTokenReq,
  GetFileObsTokenResp,
  GetMeetingAudioReq,
  GetMeetingAudioResp,
  ListMeetingReq,
  ListMeetingResp,
  ReloadMeetingAudioReq,
  ReloadMeetingAudioResp,
  ReloadMeetingOverviewReq,
  ReloadMeetingOverviewResp,
  ReloadMeetingCardSummaryReq,
  ReloadMeetingCardSummaryResp,
  UpdateMeetingReq,
  UpdateMeetingResp,
  UpdateRecognitionReq,
  UpdateRecognitionResp,
  CreateMeetingFeedbackReq,
  CreateMeetingFeedbackResp,
  FavoriteReq,
  FavoriteResp,
  TrashReq,
  TrashResp,
  TranslateOverviewReq,
  TranslateOverviewResp,
  ChatOverviewMessagesReq,
  ChatOverviewMessagesResp,
  StopStatus,
  StopChatOverviewReq,
  StopChatOverviewResp,
  StopAIChatReq,
  StopAIChatResp,
  GetEditOverviewDescReq,
  GetEditOverviewDescResp,
  ChatOverviewMessagesHistoryReq,
  ChatOverviewMessagesHistoryResp,
  UserLoginOutReq,
  UserLoginOutResp,
  UserLoginReq,
  UserLoginResp,
  GetUserInfoReq,
  GetUserInfoResp,
  UserRegisterReq,
  UserRegisterResp,
  SendVerifyCodeReq,
  SendVerifyCodeResp,
  UserForgetPasswordReq,
  UserForgetPasswordResp,
  UserResetPasswordReq,
  UserResetPasswordResp,
  CreateHotWordsReq,
  CreateHotWordsResp,
  UpdateHotWordsReq,
  UpdateHotWordsResp,
  GetHotWordsReq,
  GetHotWordsResp,
  SendMeetingSummaryEmailReq,
  SendMeetingSummaryEmailResp,
  StartRecordingReq,
  StartRecordingResp,
} from '@hkgai/pb-fe-api/lib/interface/meeting_api';

import { interceptorRequestConfig, interceptorRequestError, interceptorResponseConfig, interceptorResponseError, getDeviceId, getDeviceSourceHeaders } from './request';

/**
 * @param  账号相关
 * @returns
 */
export const fetchUserLogin = async (params: UserLoginReq) => {
  return (await meetingApi.UserLogin(params)) as Promise<UserLoginResp>;
};

// web用户登出
export const fetchUserLogOut = async (params: UserLoginOutReq) => {
  return (await meetingApi.UserLoginOut(params)) as Promise<UserLoginOutResp>;
};

// 获取用户信息
export const fetchUserInfo = async (params: GetUserInfoReq) => {
  return (await meetingApi.GetUserInfo(params)) as Promise<GetUserInfoResp>;
};

//+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

/**
 * @param  会议纪要
 * @returns
 */

export const meetingApi = new MeetingApi(SERVICE_HOST, {
  timeout: REQUEST_TIMEOUT,
});

// 配置通用拦截器
meetingApi.serviceInstance.interceptors.request.use(interceptorRequestConfig, interceptorRequestError);
meetingApi.serviceInstance.interceptors.response.use(interceptorResponseConfig, interceptorResponseError);
export const fetchFileObsToken = async (params: GetFileObsTokenReq) => {
  return (await meetingApi.GetFileObsToken(params)) as Promise<GetFileObsTokenResp>;
};

/**
 * 会议列表
 * @returns
 * @param params
 */
export const fetchMeetingList = async (params: ListMeetingReq) => {
  return (await meetingApi.ListMeeting(params)) as Promise<ListMeetingResp>;
};

/**
 * 创建会议
 * @returns
 * @param params
 */
export const fetchCreateMeeting = async (params: CreateMeetingReqType) => {
  return (await meetingApi.CreateMeeting(params as unknown as CreateMeetingReq)) as Promise<CreateMeetingResp>;
};

/**
 * 删除会议
 * @returns
 * @param params
 */
export const fetchDeleteMeeting = async (params: DeleteMeetingReq) => {
  return (await meetingApi.DeleteMeeting(params)) as Promise<DeleteMeetingResp>;
};

/**
 * 会议详情
 * @returns
 * @param params
 */
export const fetchGetAudioRecognition = async (params: GetAudioRecognitionReq) => {
  return (await meetingApi.GetAudioRecognition(params)) as Promise<GetAudioRecognitionResp>;
};
/**
 * 重新生成会议overview
 * @returns
 * @param params
 */
export const fetchReloadMeetingOverview = async (params: ReloadMeetingOverviewReq) => {
  return (await meetingApi.ReloadMeetingOverview(params)) as Promise<ReloadMeetingOverviewResp>;
};

/**
 * 重新生成会议详情
 * @returns
 * @param params
 */
export const fetchReloadAudioRecognition = async (params: ReloadMeetingAudioReq) => {
  return (await meetingApi.ReloadMeetingAudio(params)) as Promise<ReloadMeetingAudioResp>;
};
/**
 * 拿会议音频
 * @returns
 * @param params
 */
export const fetchGetMeetingAudio = async (params: GetMeetingAudioReq) => {
  return (await meetingApi.GetMeetingAudio(params)) as Promise<GetMeetingAudioResp>;
};

/**
 * 更新会议详情
 * @returns
 * @param params
 */
export const fetchUpdateMeeting = async (params: UpdateMeetingReq) => {
  return (await meetingApi.UpdateMeeting(params)) as Promise<UpdateMeetingResp>;
};

/**
 * 更新会议模型结果相关详情（语音/notes/overview）
 * @returns
 * @param params
 */
export const fetchUpdateAudioRecognition = async (params: UpdateRecognitionReq) => {
  return (await meetingApi.UpdateRecognition(params)) as Promise<UpdateRecognitionResp>;
};

/** ***** -------------------------------------------------------------------------------- */

export const fetchDownloadDoc = async (title: string, data: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    axios
      .post(
        '/slate-transform/api/docx',
        { data, type: 'doc-slate-dom' },
        { headers: { 'Content-Type': 'application/json', 'X-Device-Id': getDeviceId(), ...getDeviceSourceHeaders() }, responseType: 'blob' },
      )
      .then(response => {
        if (response.data) {
          resolve(response.data);
        }
        resolve('');
      })
      .catch(error => {
        reject(error);
      });
  });
};

export const fetchMarkdown2Slate = async (title: string, data: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    axios
      .post(
        '/slate-transform/api/markdown2Slate',
        { data, type: '' },
        { headers: { 'Content-Type': 'application/json', 'X-Device-Id': getDeviceId(), ...getDeviceSourceHeaders() }, responseType: 'json' },
      )
      .then(response => {
        if (response.data && response.data.code === 200) {
          resolve(response.data.data);
        }
        resolve('');
      })
      .catch(error => {
        reject(error);
      });
  });
};

export const fetchCreateMeetingType = async (meetingName?: string, remark?: string, meetingTimeSec?: string, files?: FileInfo[], meeting_overview_type?: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    axios
      .post(
        '/meeting/api/v1/meeting?app_id=2',
        { meeting_name: meetingName, remark, meeting_time_sec: meetingTimeSec, files, meeting_overview_type },
        { headers: { 'Content-Type': 'application/json', 'X-Device-Id': getDeviceId(), ...getDeviceSourceHeaders() }, responseType: 'json' },
      )
      .then(response => {
        if (response.data && response.data.code === 200) {
          resolve(response.data.data);
        }
        resolve('');
      })
      .catch(error => {
        reject(error);
      });
  });
};

export type CreateMeetingReqType = Omit<CreateMeetingReq, 'durationSeconds'> & {
  durationSeconds?: number;
  scene?: MeetingScene;
};

export const fetchCreateMeetingTypeNew = async (params: CreateMeetingReqType) => {
  // pb-fe-api represents protobuf int64 fields as strings, but this endpoint
  // expects duration_seconds to be serialized as a JSON integer.
  return (await meetingApi.CreateMeeting(params as unknown as CreateMeetingReq)) as Promise<CreateMeetingResp>;
};

export const fetchCreateMeetingFeedback = async (params: CreateMeetingFeedbackReq) => {
  return (await meetingApi.CreateFeedback(params)) as Promise<CreateMeetingFeedbackResp>;
};

/**
 * 收藏/取消收藏会议
 * POST /meeting/api/v1/favorite
 * @returns
 * @param params
 */
export const fetchFavoriteMeeting = async (params: FavoriteReq) => {
  return (await meetingApi.Favorite(params)) as Promise<FavoriteResp>;
};

/**
 * 回收/取消回收会议
 * POST /meeting/api/v1/trash
 * @returns
 * @param params
 */
export const fetchTrashMeeting = async (params: TrashReq) => {
  return (await meetingApi.Trash(params)) as Promise<TrashResp>;
};

/**
 * 翻译会议纪要
 * POST /meeting/api/v1/translate_overview
 * @returns
 * @param params
 */
export const fetchTranslateOverview = async (params: TranslateOverviewReq) => {
  return (await meetingApi.TranslateOverview(params)) as Promise<TranslateOverviewResp>;
};

/**
 * 重新生成会议卡片摘要（三语）
 * POST /meeting/api/v1/reload_card_summary
 * @returns
 * @param params
 */
export const fetchReloadCardSummary = async (params: ReloadMeetingCardSummaryReq) => {
  return (await meetingApi.ReloadMeetingCardSummary(params)) as Promise<ReloadMeetingCardSummaryResp>;
};

/**
 * 聊天
 * POST /meeting/api/v1/chat/chat_messages
 * @returns
 * @param params
 */
export const fetchChatOverviewMessages = async (params: ChatOverviewMessagesReq) => {
  return (await meetingApi.ChatOverviewMessages(params)) as Promise<ChatOverviewMessagesResp>;
};

export type StopChatMessagesReq = StopChatOverviewReq | StopAIChatReq;
export type StopChatMessagesResp = StopChatOverviewResp & StopAIChatResp;

type CompatibleStopResponse = {
  stopStatus?: unknown;
  status?: unknown;
  stop_status?: unknown;
};

const normalizeStopStatus = (value: unknown): StopStatus | undefined => {
  if (value === StopStatus.StopStatusProcessing || value === StopStatus.StopStatusInterrupted || value === StopStatus.StopStatusAlreadyFinished) {
    return value;
  }

  const normalized = String(value ?? '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();
  if (normalized === '0' || normalized === 'processing' || normalized === 'stopstatusprocessing') {
    return StopStatus.StopStatusProcessing;
  }
  if (normalized === '1' || normalized === 'interrupted' || normalized === 'stopstatusinterrupted') {
    return StopStatus.StopStatusInterrupted;
  }
  if (normalized === '2' || normalized === 'alreadyfinished' || normalized === 'stopstatusalreadyfinished') {
    return StopStatus.StopStatusAlreadyFinished;
  }
  return undefined;
};

/**
 * Stop generation through the endpoint for the active product surface.
 *
 * Summary Editor:    POST /meeting/api/v1/chat/stop
 * Meeting Assistant: POST /meeting/api/v1/chatbot/stop
 */
export const fetchStopChatMessages = async (params: StopChatMessagesReq): Promise<StopChatMessagesResp> => {
  if ('meetingId' in params || 'messageId' in params) {
    return meetingApi.StopChatOverview(params);
  }
  return meetingApi.StopAIChat(params as StopAIChatReq);
};

/** Compatibly read protobuf camelCase, REST snake_case, and legacy status. */
export const getStopChatMessagesStatus = (response: StopChatMessagesResp): StopStatus | undefined => {
  const compatibleResponse = response as CompatibleStopResponse;
  return normalizeStopStatus(compatibleResponse.stopStatus ?? compatibleResponse.status ?? compatibleResponse.stop_status);
};

/**
 * 获取编辑描述
 * POST /meeting/api/v1/chat/edit_desc
 * @returns
 * @param params
 */
export const fetchGetEditOverviewDesc = async (params: GetEditOverviewDescReq) => {
  return (await meetingApi.GetEditOverviewDesc(params)) as Promise<GetEditOverviewDescResp>;
};

/**
 * 获取聊天历史
 * POST /meeting/api/v1/chat/chat_messages/history
 * @returns
 * @param params
 */
export const fetchChatOverviewMessagesHistory = async (params: ChatOverviewMessagesHistoryReq) => {
  return (await meetingApi.ChatOverviewMessagesHistory(params)) as Promise<ChatOverviewMessagesHistoryResp>;
};

export const fetchUserRegister = async (params: UserRegisterReq) => {
  return (await meetingApi.UserRegister(params)) as Promise<UserRegisterResp>;
};

export const fetchSendVerifyCode = async (params: SendVerifyCodeReq) => {
  return (await meetingApi.SendVerifyCode(params)) as Promise<SendVerifyCodeResp>;
};
export const fetchUserForgetPassword = async (params: UserForgetPasswordReq) => {
  return (await meetingApi.UserForgetPassword(params)) as Promise<UserForgetPasswordResp>;
};

/**
 * 创建流式会议（实时转写）
 * POST /meeting/api/v1/create_streaming_meeting
 * @returns
 * @param params
 */
export interface CreateStreamingMeetingReq {
  creator_id: string;
  name: string;
}

export interface CreateStreamingMeetingResp {
  meeting_id: string | number;
  name: string;
  creator_id: number;
  description: string;
  status: string;
  share_url: string;
  share_token: string;
  ws_url: string;
  created_at: string;
}

export const fetchCreateStreamingMeeting = async (params: CreateStreamingMeetingReq): Promise<CreateStreamingMeetingResp> => {
  const Lang = localStorage.getItem('arco-lang') || 'en-US';
  return new Promise((resolve, reject) => {
    axios
      .post(
        '/meeting/api/v1/streaming/create_meeting?app_id=2',
        { ...params },
        {
          headers: { 'Content-Type': 'application/json', 'X-Device-Id': getDeviceId(), 'X-App-Id': '2', 'X-Acc-Lang': Lang, ...getDeviceSourceHeaders() },
          responseType: 'json',
        },
      )
      .then(response => {
        const responseData = response.data?.data || response.data;
        if (responseData?.ws_url && responseData?.meeting_id) {
          resolve(responseData);
        } else {
          reject(new Error(response.data?.msg || '创建流式会议失败'));
        }
      })
      .catch(error => {
        console.error('创建流式会议错误:', error);
        reject(error);
      });
  });
};

export const fetchUserResetPassword = async (params: UserResetPasswordReq) => {
  return (await meetingApi.UserResetPassword(params)) as Promise<UserResetPasswordResp>;
};

/**
 * 获取热词
 * POST /meeting/api/v1/hot_words/get_hot_words
 * @param params
 * @returns
 */
export const fetchGetHotWords = async (params: GetHotWordsReq) => {
  return (await meetingApi.GetHotWords(params)) as Promise<GetHotWordsResp>;
};

/**
 * 创建热词
 * POST /meeting/api/v1/hot_words/hot_words
 * @param params
 * @returns
 */
export const fetchCreateHotWords = async (params: CreateHotWordsReq) => {
  return (await meetingApi.CreateHotWords(params)) as Promise<CreateHotWordsResp>;
};

/**
 * 更新热词
 * POST /meeting/api/v1/hot_words/update_hot_words
 * @param params
 * @returns
 */
export const fetchUpdateHotWords = async (params: UpdateHotWordsReq) => {
  return (await meetingApi.UpdateHotWords(params)) as Promise<UpdateHotWordsResp>;
};

export const fetchSendMeetingSummaryEmail = async (params: SendMeetingSummaryEmailReq) => {
  return (await meetingApi.SendMeetingSummaryEmail(params)) as Promise<SendMeetingSummaryEmailResp>;
};

export const fetchStartRecording = async (params: StartRecordingReq) => {
  return (await meetingApi.StartRecording(params)) as Promise<StartRecordingResp>;
};
